import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Checkbox, EmptyState, InlineError, Input } from '../../components/ui';
import { Icon } from '../../components/ui/Icon';
import {
  displayName,
  groupThreads,
  monthLabel,
  nameList,
  previewOf,
  sizeLabel,
  textWithLinks,
  threadMatches,
  ungroupThreads,
  whenLabel,
} from '../gmail/mailThread';
import type { MailMessage, MailThread } from '../gmail/mailThread';
import { useCanSendAsMailbox, useLeadMailThreads } from '../gmail/useMailbox';
import { LeadMailReply } from './LeadMailReply';
import { activityKeys } from './leadActivity';
import { trackerKeys } from './hooks';
import type { Lead } from './types';
import './leadMail.css';

/**
 * The lead's email conversation, laid out the way HubSpot lays one out.
 *
 * Kyu, with a HubSpot contact open: "observe carefully how hubspot lays out
 * their emails together with everything." What it does, and what this does:
 *
 *   - A search box on the left; the actions on the right.
 *   - "Thread email replies": one card per conversation, with the replies
 *     inside it, or one card per message when switched off.
 *   - Cards under a month heading, newest first.
 *   - A card's first line is the subject in bold with "from <name>" quiet
 *     beside it; the date on the right. Second line, "to <names>". Then the
 *     first two lines of the newest message, fading out, so a closed card
 *     already says what the email was about.
 *   - Click anywhere on the head to open it. Open, a conversation reads top
 *     to bottom, oldest first, each message with its own from / to / when.
 *     No "reply" or "sent" badge on a message: the first live thread (3 Sep)
 *     was 52 messages between two 4ocean colleagues and the prospect's team,
 *     with the sales mailbox only cc'd. "From <name>" says who; a badge that
 *     called every one of those a reply to Alex would have been wrong 52
 *     times. `direction` stays in the data for the reply flow (drop D).
 *   - Quoted history stays folded behind "Show quoted text", the way Gmail
 *     folds it, so a reply is its new words and not the whole thread again.
 *   - Reply under the conversation (drop D), for the three senders only. The
 *     reply goes out in that Gmail thread, with files if wanted. Everyone
 *     else reads; the button is not drawn for them, and the function refuses
 *     them anyway.
 *
 * What it does not copy: opens and clicks. The Hub adds no tracking pixel to
 * what it sends (send-as-mailbox), so there is nothing to count, and a row of
 * zeros would be a lie about what is being measured.
 *
 * Everything shown is text. See src/features/gmail/mailThread.ts for why an
 * inbound email is never rendered as HTML here.
 */

function Body({ text }: { text: string }) {
  return (
    <p className="lead-mail-body">
      {textWithLinks(text).map((piece, index) =>
        piece.href === undefined ? (
          <span key={index}>{piece.text}</span>
        ) : (
          <a key={index} href={piece.href} target="_blank" rel="noopener noreferrer">
            {piece.text}
          </a>
        ),
      )}
    </p>
  );
}

function Message({
  message,
  showHead,
}: {
  message: MailMessage;
  /** Off for a single-message card, whose head already says all of this. */
  showHead: boolean;
}) {
  const [quotedOpen, setQuotedOpen] = useState(false);
  return (
    <article className="lead-mail-message">
      {!showHead ? null : (
        <div className="lead-mail-message-head">
          <span className="lead-mail-message-from">{displayName(message.from)}</span>
          <span className="lead-mail-message-to">to {nameList(message.to)}</span>
          {message.cc.length === 0 ? null : (
            <span className="lead-mail-message-to">cc {nameList(message.cc)}</span>
          )}
          <span className="lead-mail-when">{whenLabel(message.date)}</span>
        </div>
      )}
      {message.body === '' ? (
        <p className="lead-mail-body lead-mail-body--empty">(no new text — quoted only)</p>
      ) : (
        <Body text={message.body} />
      )}
      {message.attachments.length === 0 ? null : (
        <ul className="lead-mail-attachments" aria-label="Attachments">
          {message.attachments.map((file, index) => (
            <li key={index} className="lead-mail-attachment">
              <span className="lead-mail-attachment-name">{file.name}</span>
              <span className="lead-mail-attachment-size">{sizeLabel(file.size)}</span>
            </li>
          ))}
        </ul>
      )}
      {message.quoted === '' ? null : (
        <div className="lead-mail-quoted">
          <button
            type="button"
            className="lead-mail-quoted-toggle"
            aria-expanded={quotedOpen}
            onClick={() => setQuotedOpen(!quotedOpen)}
          >
            {quotedOpen ? 'Hide quoted text' : 'Show quoted text'}
          </button>
          {!quotedOpen ? null : <Body text={message.quoted} />}
        </div>
      )}
    </article>
  );
}

function ThreadCard({
  thread,
  open,
  onToggle,
  lead,
  canSend,
  onSent,
}: {
  thread: MailThread;
  open: boolean;
  onToggle: () => void;
  lead: Lead;
  canSend: boolean;
  onSent: () => void;
}) {
  const { latest } = thread;
  const count = thread.messages.length;
  const [replying, setReplying] = useState(false);
  return (
    <section className={`lead-mail-card${open ? ' lead-mail-card--open' : ''}`}>
      <button type="button" className="lead-mail-card-head" aria-expanded={open} onClick={onToggle}>
        <Icon name="chevron" size="sm" className="lead-mail-chevron" />
        <span className="lead-mail-card-text">
          <span className="lead-mail-card-title">
            <span className="lead-mail-subject">{latest.subject}</span>
            <span className="lead-mail-from"> from {displayName(latest.from)}</span>
          </span>
          <span className="lead-mail-card-to">to {nameList(latest.to)}</span>
        </span>
        <span className="lead-mail-card-meta">
          {count <= 1 ? null : (
            <span className="lead-mail-count" title={`${count} emails in this conversation`}>
              {count}
            </span>
          )}
          <span className="lead-mail-when">{whenLabel(latest.date)}</span>
        </span>
      </button>

      {open ? (
        <div className="lead-mail-card-body">
          {thread.messages.map((message) => (
            <Message key={message.id} message={message} showHead={count > 1} />
          ))}
          {!canSend ? null : replying ? (
            <LeadMailReply
              lead={lead}
              thread={thread}
              onSent={onSent}
              onCancel={() => setReplying(false)}
            />
          ) : (
            <div className="lead-mail-card-actions">
              <Button variant="secondary" size="sm" onClick={() => setReplying(true)}>
                Reply
              </Button>
            </div>
          )}
        </div>
      ) : latest.body === '' ? null : (
        <p className="lead-mail-preview">{previewOf(latest.body)}</p>
      )}
    </section>
  );
}

export function LeadMailThreads({
  lead,
  onCompose,
}: {
  lead: Lead;
  /** "Create an email" — jumps to the Compose tab. */
  onCompose: () => void;
}) {
  const leadId = lead.id;
  const threadsQuery = useLeadMailThreads(leadId, true);
  const canSend = useCanSendAsMailbox();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [threaded, setThreaded] = useState(true);
  // Which cards are open. Empty means every card is closed, which is the
  // starting state: a closed card already carries subject, people, date and
  // the first lines, so the list reads as a list before anything is clicked.
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());

  const messages = threadsQuery.data?.messages ?? [];
  const threads = (threaded ? groupThreads(messages) : ungroupThreads(messages)).filter((thread) =>
    threadMatches(thread, search),
  );
  const anyOpen = threads.some((thread) => openIds.has(thread.threadId));

  function toggle(id: string) {
    const next = new Set(openIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpenIds(next);
  }

  // A reply went out: the conversation has a new message, and the timeline
  // (and the list's Last touch) a new row. Both re-read.
  function sent() {
    void threadsQuery.refetch();
    void queryClient.invalidateQueries({ queryKey: activityKeys.forLead(leadId) });
    void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
  }

  // Month headings: the first card in each month carries the heading.
  let lastMonth = '';

  return (
    <div className="lead-mail">
      <div className="lead-mail-toolbar">
        <Input
          label="Search emails"
          labelHidden
          type="search"
          placeholder="Search emails"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="lead-mail-toolbar-actions">
          <Checkbox
            label="Thread email replies"
            checked={threaded}
            onChange={(event) => {
              setThreaded(event.target.checked);
              setOpenIds(new Set());
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={threads.length === 0}
            onClick={() =>
              setOpenIds(anyOpen ? new Set() : new Set(threads.map((thread) => thread.threadId)))
            }
          >
            {anyOpen ? 'Collapse all' : 'Expand all'}
          </Button>
          <Button variant="secondary" size="sm" onClick={onCompose}>
            Create an email
          </Button>
        </div>
      </div>

      {threadsQuery.isPending ? (
        <p className="tracker-field-note">Reading the conversation from Alex’s mailbox…</p>
      ) : threadsQuery.isError ? (
        <div className="lead-mail-error">
          <InlineError>{threadsQuery.error.message}</InlineError>
          <Button variant="secondary" size="sm" onClick={() => void threadsQuery.refetch()}>
            Try again
          </Button>
        </div>
      ) : messages.length === 0 ? (
        <EmptyState
          title="No emails with this lead yet"
          description={`Emails to or from ${threadsQuery.data.lead} in Alex’s mailbox appear here, sent from the Hub or from Gmail.`}
        />
      ) : threads.length === 0 ? (
        <p className="tracker-field-note">Nothing matches “{search}”.</p>
      ) : (
        <div className="lead-mail-list">
          {threads.map((thread) => {
            const month = monthLabel(thread.latest.date);
            const heading = month === lastMonth ? null : month;
            lastMonth = month;
            return (
              <div key={thread.threadId} className="lead-mail-group">
                {heading === null ? null : <h3 className="lead-mail-month">{heading}</h3>}
                <ThreadCard
                  thread={thread}
                  open={openIds.has(thread.threadId)}
                  onToggle={() => toggle(thread.threadId)}
                  lead={lead}
                  canSend={canSend.data === true}
                  onSent={sent}
                />
              </div>
            );
          })}
          {!threadsQuery.data.truncated ? null : (
            <p className="tracker-field-note">
              Only the most recent conversations are shown. The rest are in Gmail.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
